import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
} from "@firebase/auth";
import { httpsCallable } from "@firebase/functions";
import { auth, functions } from "./firebase";
import { getDevicePushTokens } from "../setupPush";
import { diag } from "./diag";

/**
 * "Odjavi se sa svih drugih uređaja" (Postavke), e.g. when someone else knows
 * the password of a shared account.
 *
 * The server ends every login of the account and notes the time in
 * `users/{uid}.sessionsRevokedAt` (functions/src/sessions.ts). Every app that
 * is open watches that field and logs out as soon as it changes, if its own
 * login is older (auth-context). This device then signs in again with the
 * password just entered, so it stays logged in.
 */

// While this device is doing it, its own (older) login mustn't count.
let endingOthers = false;

export async function logoutOtherDevices(password: string): Promise<void> {
  const firebaseUser = auth.currentUser;
  const email = firebaseUser?.email;
  if (!firebaseUser || !email) throw new Error("Not signed in");
  endingOthers = true;
  try {
    await reauthenticateWithCredential(firebaseUser, EmailAuthProvider.credential(email, password));
    const keepTokens = await getDevicePushTokens().catch(() => []);
    await httpsCallable(functions, "logoutOtherDevices")({ keepTokens });
    // That ended this device's login too: a new one, so it stays logged in.
    await signInWithEmailAndPassword(auth, email, password);
    diag("logged out other devices");
  } finally {
    endingOthers = false;
  }
}

/** True if this device's login started before "odjavi se sa svih uređaja". */
export async function loginWasEnded(sessionsRevokedAt: string): Promise<boolean> {
  if (endingOthers) return false;
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) return false;
  try {
    const { authTime } = await firebaseUser.getIdTokenResult();
    return Date.parse(authTime) < Date.parse(sessionsRevokedAt);
  } catch (error) {
    // The login had to be renewed and the server said no: it was ended.
    const code = (error as { code?: string })?.code;
    return code === "auth/user-token-expired" || code === "auth/user-disabled";
  }
}
